import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Repeat, Play, Pause, Pencil, Trash2, ArrowLeft, Plus, CalendarClock, FastForward, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import Loader from "../Loader";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const FREQUENCY_LABEL = {
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  custom: "Custom",
};

const frequencyDisplay = (tpl) =>
  tpl.frequency_label ||
  (tpl.frequency === "custom" && tpl.interval_days
    ? `Every ${tpl.interval_days} days`
    : FREQUENCY_LABEL[tpl.frequency] || tpl.frequency);

const formatInr = (amount) => {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return "₹0.00";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(n);
};

const formatDate = (d) => {
  if (!d || d === "0000-00-00") return "—";
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const RecurringInvoices = () => {
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/all/${isLoggedIn.main_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load auto-billing schedules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCatchUp = async (tpl) => {
    const count = tpl.missed_runs || 0;
    if (count <= 0) return;

    const result = await Swal.fire({
      title: "Catch up missed invoices?",
      text: `Generate up to ${Math.min(count, 12)} overdue invoice(s) for "${tpl.template_name}".`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Catch up",
    });
    if (!result.isConfirmed) return;

    setActionId(tpl.id);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${tpl.id}/catch-up`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || "Catch-up failed");
        return;
      }
      toast.success(data.message || "Catch-up complete");
      await fetchTemplates();
    } catch {
      toast.error("Network error");
    } finally {
      setActionId(null);
    }
  };

  const runAction = async (id, path, successMsg) => {
    setActionId(id);
    try {
      const res = await fetch(`${url}/newinvoice/recurring/${id}/${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.reason === "previous_unpaid") {
          toast.warning(
            "Skipped — the last invoice from this schedule is still unpaid. Collect payment first, or edit the schedule and uncheck “Skip if unpaid”.",
            { autoClose: 9000 }
          );
        } else {
          toast.error(data.message || "Action failed");
        }
        return;
      }
      toast.success(successMsg || data.message);
      if (path === "run-now" && data.invoice?.id) {
        toast.info(`Created ${data.invoice.invoice_number}`, { autoClose: 6000 });
      }
      await fetchTemplates();
    } catch {
      toast.error("Network error");
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = (tpl) => {
    Swal.fire({
      title: "Delete billing schedule?",
      text: `"${tpl.template_name}" will be removed. Existing invoices are kept.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      setActionId(tpl.id);
      try {
        const res = await fetch(`${url}/newinvoice/recurring/${tpl.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message);
        toast.success("Billing schedule deleted");
        await fetchTemplates();
      } catch (err) {
        toast.error(err.message || "Delete failed");
      } finally {
        setActionId(null);
      }
    });
  };

  const skipBadge = (tpl) => {
    if (tpl.last_skip_reason !== "previous_unpaid") return null;
    return (
      <span className="recurring-skip-badge" title="Will retry on next run date once paid">
        Waiting for payment
      </span>
    );
  };

  const statusBadge = (status) => {
    const s = String(status || "active").toLowerCase();
    const cls =
      s === "active"
        ? "recurring-status recurring-status--active"
        : s === "paused"
          ? "recurring-status recurring-status--paused"
          : "recurring-status recurring-status--completed";
    return <span className={cls}>{s}</span>;
  };

  return (
    <div className="product-form-page product-form-page--recurring">
      <div className="oms-header product-form-page__header">
        <div className="product-form-page__title-wrap">
          <h1 className="dashboard-title">Auto-billing schedules</h1>
          <p className="product-form-page__subtitle">
            Invoices are created and sent automatically on the schedule you set. Runs daily at 8:00 AM IST.
          </p>
        </div>
        <div className="product-form-page__header-actions">
          <button
            type="button"
            className="product-form-page__back"
            onClick={() => navigate("/invoice")}
          >
            <ArrowLeft size={16} style={{ marginRight: 6 }} />
            Invoices
          </button>
          {!loading && templates.length > 0 && (
            <button
              type="button"
              className="oms-add-btn"
              onClick={() => navigate("/recurring-invoices/new")}
            >
              <Plus size={16} style={{ marginRight: 6 }} />
              New schedule
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : templates.length === 0 ? (
        <div className="recurring-empty" role="status" aria-live="polite">
          <div className="recurring-empty__icon" aria-hidden>
            <Repeat size={40} strokeWidth={1.5} />
          </div>
          <h2 className="recurring-empty__title">No auto-billing schedules yet</h2>
          <p className="recurring-empty__desc">
            Set up a billing schedule once and invoices will be generated automatically on your
            chosen frequency — checked every day at 8:00 AM IST.
          </p>
          <ul className="recurring-empty__features">
            <li>
              <CalendarClock size={16} aria-hidden />
              Weekly, monthly, quarterly, or yearly
            </li>
            <li>
              <Play size={16} aria-hidden />
              Run manually anytime with one click
            </li>
          </ul>
          <button
            type="button"
            className="oms-add-btn recurring-empty__cta"
            onClick={() => navigate("/recurring-invoices/new")}
          >
            <Plus size={18} aria-hidden />
            Set up auto-billing
          </button>
        </div>
      ) : (
        <div className="recurring-grid">
          {templates.map((tpl) => (
            <article key={tpl.id} className="recurring-card">
              <div className="recurring-card__head">
                <h3>{tpl.template_name}</h3>
                <div className="recurring-card__badges">
                  {skipBadge(tpl)}
                  {statusBadge(tpl.status)}
                </div>
              </div>
              <p className="recurring-card__customer">{tpl.customer_name}</p>

              {tpl.status === "active" && tpl.upcoming && (
                <p className="recurring-card__upcoming">
                  Next: <strong>{tpl.upcoming.formatted_amount || formatInr(tpl.upcoming.amount)}</strong>
                  {" "}on {formatDate(tpl.upcoming.date || tpl.next_run_date)}
                </p>
              )}

              {tpl.missed_runs > 0 && tpl.status === "active" && (
                <div className="recurring-card__missed">
                  <span>{tpl.missed_runs} overdue period{tpl.missed_runs > 1 ? "s" : ""}</span>
                  <button
                    type="button"
                    className="recurring-card__catchup"
                    disabled={actionId === tpl.id}
                    onClick={() => handleCatchUp(tpl)}
                  >
                    <FastForward size={14} />
                    Catch up
                  </button>
                </div>
              )}

              {tpl.last_invoice && (
                <p className="recurring-card__last">
                  Last:{" "}
                  <button
                    type="button"
                    className="recurring-card__invoice-link"
                    onClick={() => navigate(`/invoice-detail/${tpl.last_invoice.id}`)}
                  >
                    {tpl.last_invoice.invoice_number}
                    <ExternalLink size={12} />
                  </button>
                  {" · "}
                  {formatInr(tpl.last_invoice.grand_total)}
                  {tpl.delivery_status_label && (
                    <span className="recurring-card__delivery"> · {tpl.delivery_status_label}</span>
                  )}
                </p>
              )}

              <dl className="recurring-card__meta">
                <div>
                  <dt>Frequency</dt>
                  <dd>{frequencyDisplay(tpl)}</dd>
                </div>
                <div>
                  <dt>Next run</dt>
                  <dd>{formatDate(tpl.next_run_date)}</dd>
                </div>
                <div>
                  <dt>Runs</dt>
                  <dd>{tpl.runs_count || 0}</dd>
                </div>
                <div>
                  <dt>Invoice as</dt>
                  <dd>{tpl.invoice_dpstatus || "Published"}</dd>
                </div>
              </dl>

              {Array.isArray(tpl.recent_invoices) && tpl.recent_invoices.length > 0 && (
                <div className="recurring-card__history">
                  <button
                    type="button"
                    className="recurring-card__history-toggle"
                    onClick={() => setExpandedId(expandedId === tpl.id ? null : tpl.id)}
                  >
                    {expandedId === tpl.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Generation history ({tpl.recent_invoices.length})
                  </button>
                  {expandedId === tpl.id && (
                    <ul className="recurring-card__history-list">
                      {tpl.recent_invoices.map((inv) => (
                        <li key={inv.id}>
                          <button
                            type="button"
                            onClick={() => navigate(`/invoice-detail/${inv.id}`)}
                          >
                            {inv.invoice_number}
                          </button>
                          <span>{formatDate(inv.invoice_date)}</span>
                          <span>{formatInr(inv.grand_total)}</span>
                          <span className={`recurring-inv-status recurring-inv-status--${String(inv.status || "").toLowerCase()}`}>
                            {inv.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="recurring-card__actions">
                <button
                  type="button"
                  title="Run now"
                  disabled={actionId === tpl.id}
                  onClick={() => runAction(tpl.id, "run-now", "Invoice generated")}
                >
                  <Play size={16} />
                </button>
                {tpl.status === "active" ? (
                  <button
                    type="button"
                    title="Pause"
                    disabled={actionId === tpl.id}
                    onClick={() => runAction(tpl.id, "pause", "Schedule paused")}
                  >
                    <Pause size={16} />
                  </button>
                ) : tpl.status === "paused" ? (
                  <button
                    type="button"
                    title="Resume"
                    disabled={actionId === tpl.id}
                    onClick={() => runAction(tpl.id, "resume", "Schedule resumed")}
                  >
                    <Repeat size={16} />
                  </button>
                ) : null}
                <button
                  type="button"
                  title="Edit"
                  onClick={() => navigate(`/recurring-invoices/edit/${tpl.id}`)}
                >
                  <Pencil size={16} />
                </button>
                <button
                  type="button"
                  title="Delete"
                  disabled={actionId === tpl.id}
                  onClick={() => handleDelete(tpl)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <style>{`
        .product-form-page--recurring .recurring-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: clamp(0.85rem, 2vw, 1.15rem);
          padding: clamp(0.5rem, 2vw, 1rem) 0 clamp(2rem, 5vw, 3rem);
        }
        .product-form-page--recurring .recurring-card {
          border: 1px solid var(--sp-border);
          border-radius: var(--sp-radius);
          padding: clamp(0.95rem, 2vw, 1.15rem);
          background: linear-gradient(165deg, var(--sp-surface) 0%, var(--sp-bg) 100%);
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
        }
        .product-form-page--recurring .recurring-card:hover {
          border-color: rgba(99, 102, 241, 0.35);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.32);
          transform: translateY(-1px);
        }
        .product-form-page--recurring .recurring-card__head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .product-form-page--recurring .recurring-card__badges {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.35rem;
          flex-shrink: 0;
        }
        .product-form-page--recurring .recurring-skip-badge {
          font-size: 0.62rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 0.2rem 0.5rem;
          border-radius: 999px;
          font-weight: 700;
          background: rgba(249, 115, 22, 0.15);
          color: #fdba74;
          border: 1px solid rgba(249, 115, 22, 0.3);
          white-space: nowrap;
        }
        .product-form-page--recurring .recurring-card__head h3 {
          margin: 0;
          font-size: clamp(0.95rem, 0.9rem + 0.2vw, 1.05rem);
          font-weight: 650;
          color: var(--sp-text);
          letter-spacing: -0.02em;
          line-height: 1.3;
        }
        .product-form-page--recurring .recurring-card__customer {
          margin: 0.35rem 0 0.85rem;
          color: var(--sp-muted);
          font-size: var(--sp-fs-sm);
        }
        .product-form-page--recurring .recurring-card__upcoming {
          margin: -0.45rem 0 0.75rem;
          font-size: 0.8125rem;
          color: rgba(165, 180, 252, 0.95);
        }
        .product-form-page--recurring .recurring-card__upcoming strong {
          color: #c7d2fe;
        }
        .product-form-page--recurring .recurring-card__missed {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          margin: 0 0 0.75rem;
          padding: 0.45rem 0.6rem;
          border-radius: calc(var(--sp-radius) - 4px);
          background: rgba(234, 179, 8, 0.1);
          border: 1px solid rgba(234, 179, 8, 0.25);
          font-size: 0.75rem;
          color: #fde047;
        }
        .product-form-page--recurring .recurring-card__catchup {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0.25rem 0.55rem;
          border-radius: 999px;
          border: 1px solid rgba(234, 179, 8, 0.4);
          background: rgba(234, 179, 8, 0.15);
          color: #fde047;
          font-size: 0.7rem;
          font-weight: 650;
          cursor: pointer;
        }
        .product-form-page--recurring .recurring-card__catchup:hover:not(:disabled) {
          background: rgba(234, 179, 8, 0.25);
        }
        .product-form-page--recurring .recurring-card__catchup:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .product-form-page--recurring .recurring-card__last {
          margin: 0 0 0.85rem;
          font-size: 0.78rem;
          color: var(--sp-muted);
        }
        .product-form-page--recurring .recurring-card__invoice-link {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0;
          border: none;
          background: none;
          color: #a5b4fc;
          font-weight: 650;
          cursor: pointer;
        }
        .product-form-page--recurring .recurring-card__invoice-link:hover {
          color: #c7d2fe;
          text-decoration: underline;
        }
        .product-form-page--recurring .recurring-card__delivery {
          color: rgba(148, 163, 184, 0.9);
        }
        .product-form-page--recurring .recurring-card__history {
          margin: 0 0 0.85rem;
        }
        .product-form-page--recurring .recurring-card__history-toggle {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0;
          border: none;
          background: none;
          color: var(--sp-muted);
          font-size: 0.75rem;
          font-weight: 600;
          cursor: pointer;
        }
        .product-form-page--recurring .recurring-card__history-toggle:hover {
          color: #a5b4fc;
        }
        .product-form-page--recurring .recurring-card__history-list {
          list-style: none;
          margin: 0.5rem 0 0;
          padding: 0;
          border-top: 1px solid var(--sp-border);
        }
        .product-form-page--recurring .recurring-card__history-list li {
          display: grid;
          grid-template-columns: 1fr auto auto auto;
          gap: 0.5rem;
          align-items: center;
          padding: 0.45rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          font-size: 0.72rem;
        }
        .product-form-page--recurring .recurring-card__history-list li button {
          padding: 0;
          border: none;
          background: none;
          color: #a5b4fc;
          font-weight: 650;
          text-align: left;
          cursor: pointer;
        }
        .product-form-page--recurring .recurring-card__history-list li button:hover {
          text-decoration: underline;
        }
        .product-form-page--recurring .recurring-inv-status {
          font-size: 0.62rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          font-weight: 700;
        }
        .product-form-page--recurring .recurring-inv-status--paid { color: #86efac; }
        .product-form-page--recurring .recurring-inv-status--pending { color: #fde047; }
        .product-form-page--recurring .recurring-inv-status--overdue { color: #fca5a5; }
        .product-form-page--recurring .recurring-card__meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.55rem 1rem;
          margin: 0 0 1rem;
          font-size: 0.8125rem;
        }
        .product-form-page--recurring .recurring-card__meta dt {
          color: var(--sp-muted);
          font-weight: 550;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .product-form-page--recurring .recurring-card__meta dd {
          margin: 0.15rem 0 0;
          font-weight: 650;
          color: var(--sp-text);
        }
        .product-form-page--recurring .recurring-card__actions {
          display: flex;
          gap: 0.45rem;
          border-top: 1px solid var(--sp-border);
          padding-top: 0.75rem;
        }
        .product-form-page--recurring .recurring-card__actions button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: 1px solid var(--sp-border);
          border-radius: calc(var(--sp-radius) - 4px);
          background: rgba(255, 255, 255, 0.04);
          color: var(--sp-text);
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .product-form-page--recurring .recurring-card__actions button:hover:not(:disabled) {
          background: rgba(99, 102, 241, 0.15);
          border-color: rgba(99, 102, 241, 0.4);
          color: #a5b4fc;
        }
        .product-form-page--recurring .recurring-card__actions button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .product-form-page--recurring .recurring-status {
          flex-shrink: 0;
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 0.22rem 0.55rem;
          border-radius: 999px;
          font-weight: 700;
        }
        .product-form-page--recurring .recurring-status--active {
          background: rgba(34, 197, 94, 0.15);
          color: #86efac;
          border: 1px solid rgba(34, 197, 94, 0.25);
        }
        .product-form-page--recurring .recurring-status--paused {
          background: rgba(234, 179, 8, 0.15);
          color: #fde047;
          border: 1px solid rgba(234, 179, 8, 0.25);
        }
        .product-form-page--recurring .recurring-status--completed {
          background: rgba(255, 255, 255, 0.06);
          color: var(--sp-muted);
          border: 1px solid var(--sp-border);
        }
        .product-form-page--recurring .recurring-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          margin: clamp(1.5rem, 5vw, 2.5rem) auto 0;
          padding: clamp(2.5rem, 6vw, 3.5rem) clamp(1.25rem, 4vw, 2rem);
          max-width: 28rem;
          border-radius: calc(var(--sp-radius) + 4px);
          border: 1px dashed rgba(255, 255, 255, 0.14);
          background: linear-gradient(165deg, rgba(14, 20, 56, 0.95) 0%, rgba(22, 29, 71, 0.85) 100%);
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.3);
        }
        .product-form-page--recurring .recurring-empty__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 4.5rem;
          height: 4.5rem;
          margin-bottom: 1.25rem;
          border-radius: calc(var(--sp-radius) + 2px);
          background: rgba(99, 102, 241, 0.12);
          color: #818cf8;
          box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.25);
        }
        .product-form-page--recurring .recurring-empty__title {
          margin: 0;
          font-size: clamp(1.05rem, 1rem + 0.3vw, 1.25rem);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--sp-text);
        }
        .product-form-page--recurring .recurring-empty__desc {
          margin: 0.65rem 0 0;
          font-size: var(--sp-fs-sm);
          color: var(--sp-muted);
          line-height: 1.55;
          max-width: 22rem;
        }
        .product-form-page--recurring .recurring-empty__features {
          list-style: none;
          margin: 1.35rem 0 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          width: 100%;
          max-width: 20rem;
        }
        .product-form-page--recurring .recurring-empty__features li {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.8125rem;
          font-weight: 550;
          color: rgba(238, 241, 255, 0.72);
          text-align: left;
        }
        .product-form-page--recurring .recurring-empty__features li svg {
          flex-shrink: 0;
          color: #818cf8;
        }
        .product-form-page--recurring .recurring-empty__cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          margin-top: 1.65rem;
          min-height: var(--sp-touch);
          padding: 0.6rem 1.5rem;
          font-size: var(--sp-fs-sm);
          font-weight: 650;
          border-radius: var(--sp-radius);
          background: linear-gradient(135deg, #6366f1, #4f46e5) !important;
          border: none;
          box-shadow: 0 6px 24px rgba(99, 102, 241, 0.35);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .product-form-page--recurring .recurring-empty__cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 32px rgba(99, 102, 241, 0.42);
        }
        .product-form-page--recurring .product-form-page__header-actions .oms-add-btn {
          display: inline-flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
};

export default RecurringInvoices;
